import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import { 
  Mail, Lock, Eye, EyeOff, ArrowRight, Loader, 
  AlertCircle, CheckCircle, Sparkles, TrendingUp,
  BarChart3, Zap
} from 'lucide-react';
import './Auth.css';

const LoginPage = ({ onNavigate, onSuccess }) => {
  const { login, loading, error } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    if (!email) {
      setFormError('Email is required');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError('Please enter a valid email');
      return false;
    }
    if (!password) {
      setFormError('Password is required');
      return false;
    }
    if (password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!validateForm()) return;

    setIsSubmitting(true);
    const result = await login(email, password, rememberMe);
    setIsSubmitting(false);

    if (result.success) {
      if (onSuccess) onSuccess();
    } else {
      setFormError(result.error || 'Login failed. Please try again.');
    }
  };

  const handleDemoLogin = async () => {
    setIsSubmitting(true);
    const result = await login('demo@elemental.io', 'demo123', false);
    setIsSubmitting(false);
    
    if (result.success && onSuccess) {
      onSuccess();
    }
  };

  return (
    <div className="auth-page">
      {/* Background Effects */}
      <div className="auth-bg">
        <div className="auth-bg-gradient" />
        <div className="auth-bg-grid" />
        <div className="auth-bg-glow auth-bg-glow-1" />
        <div className="auth-bg-glow auth-bg-glow-2" />
      </div>

      <div className="auth-container">
        {/* Left Panel - Branding */}
        <div className="auth-branding">
          <div className="auth-brand-content">
            <div className="auth-logo">
              <div className="auth-logo-icon">
                <TrendingUp size={32} />
              </div>
              <span className="auth-logo-text">ELEMENTAL</span>
            </div>
            
            <h1 className="auth-tagline">
              Professional Trading
              <span className="auth-tagline-highlight">Reimagined</span>
            </h1>
            
            <p className="auth-description">
              Advanced charting, AI-powered analysis, and institutional-grade tools 
              for serious traders.
            </p>

            <div className="auth-features">
              <div className="auth-feature">
                <BarChart3 size={20} />
                <span>Real-time Charts</span>
              </div>
              <div className="auth-feature">
                <Sparkles size={20} />
                <span>AI Analysis</span>
              </div>
              <div className="auth-feature">
                <Zap size={20} />
                <span>Pattern Detection</span>
              </div>
            </div>
          </div>

          <div className="auth-brand-footer">
            <p>Trusted by 10,000+ traders worldwide</p>
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="auth-form-panel">
          <div className="auth-form-container">
            <div className="auth-form-header">
              <h2>Welcome back</h2>
              <p>Sign in to your account to continue</p>
            </div>

            {(formError || error) && (
              <div className="auth-alert auth-alert-error">
                <AlertCircle size={18} />
                <span>{formError || error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-input-group">
                <label htmlFor="email">Email</label>
                <div className="auth-input-wrapper">
                  <Mail size={18} className="auth-input-icon" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="email"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="auth-input-group">
                <label htmlFor="password">Password</label>
                <div className="auth-input-wrapper">
                  <Lock size={18} className="auth-input-icon" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    disabled={isSubmitting}
                  />
                  <button
                    type="button"
                    className="auth-input-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="auth-options">
                <label className="auth-checkbox">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <span className="auth-checkbox-mark" />
                  <span>Remember me</span>
                </label>

                <button
                  type="button"
                  className="auth-link"
                  onClick={() => onNavigate && onNavigate('forgot-password')}
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                className="auth-btn auth-btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader size={18} className="auth-spinner" />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>

            <div className="auth-divider">
              <span>or</span>
            </div>

            <button
              type="button"
              className="auth-btn auth-btn-demo"
              onClick={handleDemoLogin}
              disabled={isSubmitting}
            >
              <Sparkles size={18} />
              <span>Try Demo Account</span>
            </button>

            <div className="auth-footer">
              <span>Don't have an account?</span>
              <button
                type="button"
                className="auth-link"
                onClick={() => onNavigate && onNavigate('signup')}
              >
                Create account
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;